import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { debounce, preloadImage } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.presentation === 'grid') {
      this.#initHoverZoom(signal);
      this.#initSiblingSwatchPreview();
    }

    this.#initMobileArrowsCounter(signal);
  }

  #controller = new AbortController();

  #swatchController = new AbortController();

  /** @type {HTMLImageElement | null} */
  #originalImg = null;

  /** @type {number | null} */
  #previewIndex = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#debouncedRestore.cancel();
    this.#restoreOriginalImage();
    this.#controller.abort();
    this.#swatchController.abort();
  }

  updatedCallback() {
    super.updatedCallback();

    // Discard stale preview state — #originalImg from the previous product is
    // no longer valid after morph replaces the gallery nodes.
    this.#debouncedRestore.cancel();
    this.#originalImg = null;
    this.#previewIndex = null;

    // Re-attach swatch listeners — abort first to avoid duplicates on surviving nodes.
    if (this.presentation === 'grid') {
      this.#swatchController.abort();
      this.#swatchController = new AbortController();
      this.#initSiblingSwatchPreview();
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Initialises sibling swatch hover preview for grid presentation.
   * Finds the sibling-products element (variant-picker rendering path)
   * in the same section and attaches pointerenter/pointerleave listeners
   * to each swatch label.
   */
  #initSiblingSwatchPreview() {
    const section = this.closest('.shopify-section');
    const siblingSwatches = section?.querySelector('sibling-products');

    if (!siblingSwatches) return;

    const { signal } = this.#swatchController;
    const labels = siblingSwatches.querySelectorAll('label');

    for (const label of labels) {
      label.addEventListener('pointerenter', this.#handleSwatchPointerEnter, { signal });
      label.addEventListener('pointerleave', this.#handleSwatchPointerLeave, { signal });
    }

    this.#preloadSiblingImages(siblingSwatches);
  }

  /**
   * Preloads featured images for all non-checked sibling swatches.
   *
   * @param {Element} siblingSwatches - The sibling swatch container element.
   */
  #preloadSiblingImages(siblingSwatches) {
    const inputs = siblingSwatches.querySelectorAll('input[data-featured-image-url]:not(:checked)');

    for (const input of inputs) {
      const url = input.dataset.featuredImageUrl;
      if (url) preloadImage(url);
    }
  }

  /**
   * Returns the current grid column count from computed styles.
   *
   * @returns {number} The number of grid columns, or 0 if grid not found.
   */
  #getGridColumnCount() {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return 0;

    const columns = getComputedStyle(grid).gridTemplateColumns;
    return columns.split(/\s+/).filter(Boolean).length;
  }

  /**
   * Handles pointerenter on a swatch label. Swaps the last image in the
   * first grid row with the hovered sibling's featured image.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleSwatchPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    const input = event.currentTarget.querySelector('input');
    if (!input || input.checked) return;

    const imageUrl = input.dataset.featuredImageUrl;
    if (!imageUrl) return;

    const columnCount = this.#getGridColumnCount();
    if (columnCount < 2) return;

    const targetIndex = columnCount - 1;
    const mediaItems = this.refs.media;
    if (!mediaItems || targetIndex >= mediaItems.length) return;

    const targetContainer = mediaItems[targetIndex];
    const img = targetContainer.querySelector('.product-media__image');
    if (!img || img.tagName !== 'IMG') return;

    // Cancel pending restore only when a new preview will definitely be shown.
    // Placing cancel() before guards would suppress the debounced restore when
    // an early return fires (e.g., hovering the checked swatch after a non-checked one).
    this.#debouncedRestore.cancel();

    if (!this.#originalImg || this.#previewIndex !== targetIndex) {
      this.#restoreOriginalImage();
      this.#originalImg = /** @type {HTMLImageElement} */ (img.cloneNode(false));
      this.#previewIndex = targetIndex;
    }

    img.src = imageUrl;
    img.srcset = '';
    img.alt = input.ariaLabel || '';
  };

  /**
   * Handles pointerleave on a swatch label. Triggers debounced restore
   * of the original image.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleSwatchPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#debouncedRestore();
  };

  /**
   * Restores the original image to the previewed grid slot.
   */
  #restoreOriginalImage = () => {
    if (!this.#originalImg || this.#previewIndex === null) return;

    const mediaItems = this.refs.media;
    if (!mediaItems || this.#previewIndex >= mediaItems.length) {
      this.#originalImg = null;
      this.#previewIndex = null;
      return;
    }

    const targetContainer = mediaItems[this.#previewIndex];
    const img = targetContainer.querySelector('.product-media__image');

    if (img && img.tagName === 'IMG') {
      img.src = this.#originalImg.src;
      img.srcset = this.#originalImg.srcset;
      img.alt = this.#originalImg.alt;
    }

    this.#originalImg = null;
    this.#previewIndex = null;
  };

  /** @type {ReturnType<typeof debounce> & { cancel(): void }} */
  #debouncedRestore = debounce(this.#restoreOriginalImage, 100);

  /**
   * Restores any active hover preview image and cancels the debounced restore.
   * Called by sibling-products before cache recapture to avoid poisoning the snapshot.
   */
  clearSwatchPreview() {
    this.#debouncedRestore.cancel();
    this.#restoreOriginalImage(); // restores DOM to original image, then nulls #originalImg + #previewIndex
  }

  /**
   * Re-attaches swatch hover preview listeners after a section morph.
   * Required for cached morphs where MG survives in-place and no lifecycle callback fires.
   */
  reinitSwatchPreview() {
    if (this.presentation !== 'grid') return;
    this.#swatchController.abort();
    this.#swatchController = new AbortController();
    this.#initSiblingSwatchPreview();
  }

  /**
   * Initialises desktop hover zoom for grid image containers.
   * Finds all image cells with a zoom button (block_settings.zoom enabled)
   * and attaches pointer event listeners for in-place zoom.
   *
   * @param {AbortSignal} signal - The abort signal for cleanup.
   */
  #initHoverZoom(signal) {
    const containers = this.querySelectorAll('.product-media-container');

    for (const container of containers) {
      if (!container.querySelector('.product-media-container__zoom-button')) continue;

      container.addEventListener('pointerenter', this.#handleHoverZoomEnter, { signal });
      container.addEventListener('pointermove', this.#handleHoverZoomMove, { signal });
      container.addEventListener('pointerleave', this.#handleHoverZoomLeave, { signal });
    }
  }

  /**
   * Calculates cursor position as a clamped percentage within a container.
   *
   * @param {PointerEvent} event - The pointer event.
   * @param {Element} container - The container element.
   * @returns {{ x: number, y: number }} Percentage coordinates clamped to 0–100.
   */
  #getZoomCoordinates(event, container) {
    const rect = container.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    return { x, y };
  }

  /**
   * Handles pointerenter on a grid image container. Activates hover zoom
   * and sets initial transform-origin coordinates.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleHoverZoomEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    const container = event.currentTarget;
    const { x, y } = this.#getZoomCoordinates(event, container);

    container.style.setProperty('--hover-zoom-x', `${x}%`);
    container.style.setProperty('--hover-zoom-y', `${y}%`);
    container.setAttribute('data-hover-zoom', '');
  };

  /**
   * Handles pointermove on a grid image container. Updates the
   * transform-origin coordinates to follow the cursor.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleHoverZoomMove = (event) => {
    if (event.pointerType !== 'mouse') return;

    const container = event.currentTarget;
    const { x, y } = this.#getZoomCoordinates(event, container);

    container.style.setProperty('--hover-zoom-x', `${x}%`);
    container.style.setProperty('--hover-zoom-y', `${y}%`);
  };

  /**
   * Handles pointerleave on a grid image container. Deactivates hover zoom.
   * CSS custom properties are NOT cleared so the zoom-out animation plays
   * from the last cursor position.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleHoverZoomLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    event.currentTarget.removeAttribute('data-hover-zoom');
  };

  /**
   * Initialises the mobile arrows counter sync. Observes the standard
   * slideshow counter (ref="current") and mirrors its text content to
   * the mobile overlay counter (data-mobile-arrows-current).
   *
   * @param {AbortSignal} signal - The abort signal for cleanup.
   */
  #initMobileArrowsCounter(signal) {
    const mobileCounter = this.querySelector('[data-mobile-arrows-current]');
    if (!mobileCounter) return;

    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    const standardCounter = slideshow.querySelector('[ref="current"]');
    if (!standardCounter) return;

    const observer = new MutationObserver(() => {
      mobileCounter.textContent = standardCounter.textContent;
    });

    observer.observe(standardCounter, { childList: true });

    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
